import React from 'react';
import { campo } from './campo';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    label?: string;
    error?: string;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
}

/**
 * La rueda del ratón NO debe cambiar un número.
 *
 * Un `<input type="number">` con el foco puesto se traga el evento de rueda y
 * mueve su valor en vez de mover la página. En un formulario corto casi no se
 * nota; en una página larga —Gastos fijos, el asistente— el usuario escribe una
 * cifra, intenta bajar, y la página no se mueve: parece trabada. Peor: el
 * número cambió sin que nadie lo viera, y se guarda con el resto.
 *
 * Quitarle el foco al empezar a girar la rueda arregla las dos cosas: el scroll
 * vuelve a la página y el valor se queda como se escribió. El campo ya habrá
 * confirmado su valor en el `blur`, así que no se pierde nada.
 */
export function blurOnWheel(e: React.WheelEvent<HTMLInputElement>) {
    if (e.currentTarget.type !== 'number') return;
    if (document.activeElement === e.currentTarget) e.currentTarget.blur();
}

/**
 * Input — Sistema 2.0.
 * Fondo blanco siempre, borde acero-300, radio 12px.
 * Foco = la firma de la marca: borde navy + halo amarillo.
 * Error = inline, el campo conserva el valor.
 * Label = etiqueta única del sistema (.label-ui).
 */
export const Input = React.forwardRef<HTMLInputElement, InputProps>(
    ({ className = '', label, error, leftIcon, rightIcon, id, onWheel, ...props }, ref) => {
        const reactId = React.useId();
        const inputId = id || `input-${reactId}`;

        return (
            <div className={`flex flex-col gap-1.5 w-full ${className}`}>
                {label && (
                    <label htmlFor={inputId} className="label-ui ml-1">
                        {label}
                    </label>
                )}
                <div className="relative flex items-center w-full">
                    {leftIcon && (
                        <div className="absolute left-3 text-acero-400 pointer-events-none">
                            {leftIcon}
                        </div>
                    )}
                    <input
                        ref={ref}
                        id={inputId}
                        aria-invalid={error ? true : undefined}
                        onWheel={(e) => { blurOnWheel(e); onWheel?.(e); }}
                        /* Las clases viven en `campo.ts`, no aquí: este componente
                           era el único que las tenía bien, y por estar encerradas
                           dentro nadie más podía usarlas — de ahí las 12 copias
                           escritas a mano por la app. El relleno sí se decide
                           aquí, porque depende de si hay íconos. */
                        className={campo({
                            error: !!error,
                            relleno: `${leftIcon ? 'pl-10' : 'pl-4'} ${rightIcon ? 'pr-10' : 'pr-4'}`,
                        })}
                        {...props}
                    />
                    {rightIcon && (
                        <div className="absolute right-3 text-acero-400">
                            {rightIcon}
                        </div>
                    )}
                </div>
                {error && (
                    <p className="text-xs text-peligro ml-1">{error}</p>
                )}
            </div>
        );
    }
);

Input.displayName = 'Input';
